"use client";

import { useEffect, useLayoutEffect, useRef } from "react";
import { useRouter } from "next/navigation";

import { isTypingTarget, readPageShortcut } from "@/lib/keyboard";

/**
 * Option/Alt + a page's letter navigates directly to that page. Keys typed
 * into a field are left alone.
 *
 * @param {ReturnType<import("@/lib/navigation/nav-items").collectNavShortcuts>} shortcuts
 */
export function useNavShortcuts(shortcuts) {
  const router = useRouter();
  // Read through a ref so the listener is attached once, not on every render
  // of the nav. Written on commit, so a render React discards never leaks in.
  const shortcutsRef = useRef(shortcuts);
  useLayoutEffect(() => {
    shortcutsRef.current = shortcuts;
  }, [shortcuts]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (isTypingTarget(event.target)) return;
      const letter = readPageShortcut(event);
      const target = letter
        ? shortcutsRef.current.find((shortcut) => shortcut.letter === letter)
        : null;
      if (!target) return;

      event.preventDefault();
      router.push(target.href);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [router]);
}
